import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText, Calendar, MessageSquare, CheckCircle,
  ArrowRight, GraduationCap, School, Sparkles, Users, KeyRound,
} from 'lucide-react';

const stepsConfig = [
  { key: 's1', number: '01', icon: FileText },
  { key: 's2', number: '02', icon: Calendar },
  { key: 's3', number: '03', icon: MessageSquare },
  { key: 's4', number: '04', icon: CheckCircle },
];

const levelsConfig = [
  { key: 'college', icon: School, color: 'bg-blue-500' },
  { key: 'lycee', icon: GraduationCap, color: 'bg-amber-500' },
];

const bacSpecialities = [
  { code: 'maths', label: 'Mathématiques' },
  { code: 'physics', label: 'Sciences Physiques' },
  { code: 'svt', label: 'SVT' },
  { code: 'ses', label: 'SES' },
  { code: 'hggsp', label: 'HGGSP' },
  { code: 'hlp', label: 'HLP' },
  { code: 'llce', label: 'LLCE' },
];

export const Admissions = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const requirements = t('admissions.requirements', { returnObjects: true });

  return (
    <section id="admissions" data-testid="admissions-section" ref={ref} className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
            {t('admissions.tag')}
          </span>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 mb-6">
            {t('admissions.title1')} <span className="italic">{t('admissions.title1Italic')}</span>
          </h2>
          <div className="section-divider mb-6" />
          <p className="font-inter text-slate-600 text-lg max-w-2xl mx-auto">
            {t('admissions.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mb-20">
          {levelsConfig.map((level, index) => (
            <motion.div
              key={level.key}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              data-testid={`level-card-${level.key}`}
              className="bg-slate-50 rounded-2xl p-8 card-hover border border-slate-100"
            >
              <div className="flex items-start gap-6">
                <div className={`w-16 h-16 rounded-xl ${level.color} flex items-center justify-center flex-shrink-0`}>
                  <level.icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-3 mb-2">
                    <h3 className="font-playfair text-2xl font-bold text-slate-900">
                      {t(`admissions.levels.${level.key}.title`)}
                    </h3>
                    <span className="text-slate-500 text-sm">
                      ({t(`admissions.levels.${level.key}.subtitle`)})
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="inline-block bg-white text-slate-700 text-sm font-medium px-3 py-1 rounded-full border border-slate-200">
                      {t(`admissions.levels.${level.key}.grades`)}
                    </span>
                    <span
                      data-testid={`level-small-classes-${level.key}`}
                      className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-sm font-medium px-3 py-1 rounded-full border border-amber-200"
                    >
                      <Users className="w-3.5 h-3.5" />
                      {t('admissions.smallClasses')}
                    </span>
                  </div>
                  <p className="font-inter text-slate-600 leading-relaxed mb-5">
                    {t(`admissions.levels.${level.key}.description`)}
                  </p>
                  <button
                    type="button"
                    data-testid={`level-visit-btn-${level.key}`}
                    onClick={() => {
                      const el = document.querySelector('#contact');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-full px-5 py-2.5 transition-colors group"
                  >
                    {t('admissions.requestVisit')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bac Specialities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.35 }}
          data-testid="bac-specialities-card"
          className="mb-20 bg-gradient-to-br from-slate-50 to-amber-50/40 rounded-2xl p-8 border border-amber-100"
        >
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-playfair text-xl lg:text-2xl font-bold text-slate-900 mb-1">
                {t('admissions.bacSpecialitiesTitle')}
              </h3>
              <p className="font-inter text-slate-600 text-sm">
                {t('admissions.bacSpecialitiesSubtitle')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 lg:gap-3">
            {bacSpecialities.map((spec) => (
              <span
                key={spec.code}
                data-testid={`bac-spec-${spec.code}`}
                className="bg-white border border-slate-200 text-slate-800 text-sm font-medium px-4 py-2 rounded-full hover:border-amber-400 hover:bg-amber-50 transition-colors"
              >
                {spec.label}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12"
        >
          <h3 className="font-playfair text-2xl font-bold text-slate-900 text-center mb-12">
            {t('admissions.processTitle')}
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stepsConfig.map((step, index) => (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                data-testid={`admission-step-${index + 1}`}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-6 border border-slate-200 h-full card-hover">
                  <span className="font-playfair text-5xl font-bold text-slate-100 absolute top-4 right-4">
                    {step.number}
                  </span>
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-4">
                      <step.icon className="w-6 h-6 text-amber-400" />
                    </div>
                    <h4 className="font-playfair text-lg font-semibold text-slate-900 mb-2">
                      {t(`admissions.steps.${step.key}.title`)}
                    </h4>
                    <p className="font-inter text-slate-600 text-sm leading-relaxed">
                      {t(`admissions.steps.${step.key}.description`)}
                    </p>
                  </div>
                </div>
                {index < stepsConfig.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* MyLafayette returning-family discreet card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          data-testid="mylafayette-card"
          className="mb-12 bg-slate-50 border border-slate-200 rounded-2xl p-6 lg:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <KeyRound className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-playfair text-base lg:text-lg font-semibold text-slate-900 mb-1">
              {t('admissions.myLafayette.title')}
            </h4>
            <p className="font-inter text-slate-600 text-sm leading-relaxed">
              {t('admissions.myLafayette.description')}
            </p>
          </div>
          <a
            href="https://student-intake-29.preview.emergentagent.com/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mylafayette-btn"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-900 hover:text-white text-slate-900 border border-slate-300 hover:border-slate-900 font-semibold rounded-full px-5 py-2.5 text-sm transition-colors flex-shrink-0"
          >
            {t('admissions.myLafayette.button')}
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-slate-900 rounded-3xl p-8 lg:p-12"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-white mb-4">
                {t('admissions.requirementsTitle')}
              </h3>
              <p className="font-inter text-slate-400 leading-relaxed mb-6">
                {t('admissions.requirementsDesc')}
              </p>
              <ul className="space-y-3">
                {(Array.isArray(requirements) ? requirements : []).map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <span className="font-inter text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center lg:text-right">
              <p className="font-caveat text-2xl text-amber-400 mb-4">
                {t('admissions.openHandwritten')}
              </p>
              <a
                href="https://mylafayetteadmissions.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admissions-apply-btn"
                className="inline-flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-full px-10 py-6 text-lg btn-shine transition-colors"
              >
                {t('admissions.applyButton')}
                <ArrowRight className="w-5 h-5 ml-2 inline-block" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
